import { ChatRunEvent } from "@/aawebsocket/websocket.gen";
import { SessionMessage } from "@/aawebsocket/chatSession";
import { chatState } from "@/app/chatState";
import { runsState } from "@/app/runsState";
import { ChatMessage } from "@/app/typing/chat";

const scopeKey = (tagIds: number[]) => [...tagIds].sort((a, b) => a - b).join("-");

/**
 * What a send from the composer becomes: a new turn, a steer into the running one,
 * or nothing (replays, skills and injections wait for the run to end).
 */
export const routeSend = (
    running: boolean,
    opts: { is_injection?: boolean; replay_message_id?: number; skill_key?: string } = {},
): "start" | "steer" | "ignore" => {
    if (!running) return "start";
    if (opts.is_injection || opts.replay_message_id || opts.skill_key) return "ignore";
    return "steer";
};

/**
 * Apply a session message scoped to the open chat (subscribe reply, snapshot, live
 * event, status) to chatState. Call it only once the scope's history is loaded.
 */
export const applySessionMessage = (message: SessionMessage, onActivity?: () => void) => {
    const inScope = (tagIds: number[]) => scopeKey(tagIds) === scopeKey(chatState.tagIds);
    switch (message.type) {
        case "subscribed":
            if (message.conversation_id !== null && chatState.conversationId === null && inScope(message.tag_ids)) {
                chatState.setConversation(chatState.conversation, message.conversation_id);
            }
            break;
        case "run_snapshot":
            if (message.conversation_id !== chatState.conversationId) break;
            if (!message.resumed) chatState.dropIncomplete();
            if (message.run) {
                if (!message.resumed) chatState.setState("awaiting_llm");
                message.events.forEach((record) => applyRunEvent(record.event, message.conversation_id));
            } else if (chatState.state !== "awaiting_backend") {
                chatState.setState("idle");
            }
            break;
        case "run_event":
            applyRunEvent(message.event, message.conversation_id, onActivity);
            break;
        case "run_status":
            if (chatState.conversationId === null && message.status === "running" && inScope(message.tag_ids)) {
                // First run of a new conversation: this is where its id shows up.
                chatState.setConversation(chatState.conversation, message.conversation_id);
            }
            if (message.conversation_id !== chatState.conversationId) break;
            if (message.status === "running") {
                if (chatState.state === "idle" || chatState.state === "awaiting_backend") chatState.setState("awaiting_llm");
            } else {
                chatState.finalizeIncomplete();
                chatState.clearQueuedSteers();
                chatState.setState("idle");
                runsState.markSeen(message.conversation_id);
            }
            break;
    }
};

export const isScopedSessionMessage = (message: SessionMessage) =>
    message.type === "subscribed" || message.type === "run_snapshot" || message.type === "run_event" || message.type === "run_status";

const messageText = (content: ChatMessage["content"]): string => {
    if (typeof content === "string") return content;
    return content.map((block) => ("text" in block && typeof block.text === "string" ? block.text : "")).join("");
};

/**
 * Apply one streamed event of the focused conversation's run to chatState.
 * Events for other conversations are ignored: their state is reloaded on reopen.
 */
export const applyRunEvent = (event: ChatRunEvent, conversationId: number, onActivity?: () => void) => {
    if (conversationId !== chatState.conversationId) return;

    switch (event.type) {
        case "message": {
            chatState.setState("streaming");
            onActivity?.();
            const detail = event.detail;
            if (detail.incomplete) {
                // Text after a tool call in the same response gets its own placeholder, not the tool's JSON buffer.
                const live = chatState.livePlaceholder();
                const liveIsTool = Array.isArray(live?.content) && live.content[0]?.type === "tool_use";
                if ((!chatState.streamingContent && !live) || liveIsTool) {
                    chatState.startStreaming(detail.agent_id, detail.conversation_id);
                }
                chatState.appendStreamingContent(detail.content);
            } else {
                if (detail.is_injection) break;
                if (detail.role === "user") chatState.takeQueuedSteer(messageText(detail.content as ChatMessage["content"]));
                chatState.commitStreaming({ ...(detail as ChatMessage), conversation_id: detail.conversation_id });
            }
            break;
        }
        case "tool_start":
            chatState.setState("streaming");
            chatState.startStreaming(undefined, event.detail.conversation_id, { name: event.detail.tool_name, id: event.detail.tool_id });
            break;
        case "tool_input_partial":
            chatState.setState("streaming");
            chatState.appendStreamingContent(event.detail.partial_json);
            break;
        case "status":
            switch (event.detail) {
                case "message_start":
                case "message_delta":
                    chatState.setState("streaming");
                    break;
                case "conversation_locked":
                    chatState.setState("conversation_locked");
                    break;
                case "awaiting_llm":
                    chatState.setState("awaiting_llm");
                    break;
                case "awaiting_event_summarisation":
                    chatState.setState("awaiting_event_summarisation");
                    break;
                case "awaiting_document_summarisation":
                    chatState.setState("awaiting_document_summarisation");
                    break;
            }
            break;
        case "action": {
            const res = event.detail;
            switch (res.tool) {
                case "set_selected_objects_tool":
                    chatState.chatObjects.update(res.objects.map((o) => ({ key: { type: o.object_type, id: o.object_id }, value: { isSelected: true } })));
                    chatState.setSelectedObjectKey({ type: res.objects[0].object_type, id: res.objects[0].object_id }, true);
                    break;
                case "artifact_variable_name_update":
                    chatState.chatObjects.update([{ key: { type: "artifact", id: res.artifact_id }, value: { artifact: { variable_name: res.variable_name } } }]);
                    break;
                case "object_created":
                    chatState.chatObjects.update([
                        {
                            key: { type: res.object_type, id: res.object_id },
                            value: {
                                id: res.object_id,
                                type: res.object_type,
                                label: res.label,
                                isSelected: true,
                                edit: true,
                                created_at: new Date().toISOString(),
                                unsaved_changes: false,
                                parent: res.parent ?? undefined,
                                artifact: res.object_type === "artifact" ? { is_draft: res.is_draft } : undefined,
                            },
                        },
                    ]);
                    if (res.select) {
                        chatState.setSelectedObjectKey({ type: res.object_type, id: res.object_id }, false);
                    }
                    break;
            }
            break;
        }
        case "error":
            // Retried errors mid-run are informational; the run's status says when it ended.
            console.warn("chat run error", event.detail);
            break;
    }
};
